import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Send, Mail } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

export function ContactUserModal({ open, onOpenChange, users = [], contextType = '', contextId = '', API, token }) {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [selectedUsers, setSelectedUsers] = useState({});
  const [sending, setSending] = useState(false);

  const toggleUser = (id) => {
    setSelectedUsers(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSend = async () => {
    if (!subject.trim()) { toast.error('Veuillez saisir un sujet'); return; }
    if (!message.trim()) { toast.error('Veuillez saisir un message'); return; }
    const recipients = users.filter(u => selectedUsers[u.id]).map(u => ({
      email: u.email,
      name: u.name,
      id: u.id
    }));
    if (recipients.length === 0) { toast.error('Sélectionnez au moins un destinataire'); return; }

    setSending(true);
    try {
      await axios.post(`${API}/admin/contact-user`, {
        subject, message, recipients, context_type: contextType, context_id: contextId
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Email envoyé à ${recipients.length} destinataire(s)`);
      setSubject('');
      setMessage('');
      setSelectedUsers({});
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Contacter l'utilisateur
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <Label className="text-sm font-medium mb-2 block">Destinataire(s)</Label>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {users.map(u => (
                <div key={u.id} className="flex items-center gap-2">
                  <Checkbox
                    id={`contact-${u.id}`}
                    checked={!!selectedUsers[u.id]}
                    onCheckedChange={() => toggleUser(u.id)}
                    data-testid={`contact-check-${u.id}`}
                  />
                  <Label htmlFor={`contact-${u.id}`} className="text-sm cursor-pointer">
                    <span className="font-medium">{u.role === 'buyer' ? 'Acheteur' : u.role === 'seller' ? 'Vendeur' : u.role}</span>
                    {' - '}{u.name} ({u.email})
                  </Label>
                </div>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-sm font-medium mb-1 block">Sujet</Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Sujet du message..."
              data-testid="contact-subject"
            />
          </div>
          <div>
            <Label className="text-sm font-medium mb-1 block">Message</Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Votre message..."
              rows={5}
              data-testid="contact-message"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Envoyé depuis support@kacos.ca. Le message sera enregistré dans la section Support pour le suivi.
          </p>
          <Button
            className="w-full"
            onClick={handleSend}
            disabled={sending || !subject.trim() || !message.trim()}
            data-testid="contact-send-btn"
          >
            {sending ? 'Envoi en cours...' : 'Envoyer'}
            <Send className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
